const BAF_CART_CODE_DEADLINE_KEY = 'baf-cart-code-foryou10-deadline-v1';
const BAF_CART_CODE_DURATION = 10 * 60 * 1000;
const BAF_CART_CODE_DEADLINE_EVENT = 'baf:cart-code-deadline';
let bafCartCodeDeadline = 0;

function parseCartCodeDeadline(value) {
  const deadline = Number(value);
  return Number.isFinite(deadline) && deadline > 0 ? deadline : 0;
}

function readCartCodeDeadline() {
  for (const name of ['localStorage', 'sessionStorage']) {
    try {
      const deadline = parseCartCodeDeadline(window[name].getItem(BAF_CART_CODE_DEADLINE_KEY));
      if (deadline && (!bafCartCodeDeadline || deadline < bafCartCodeDeadline)) bafCartCodeDeadline = deadline;
    } catch (_) {}
  }
  return bafCartCodeDeadline;
}

function saveCartCodeDeadline(candidate) {
  const previous = bafCartCodeDeadline;
  const existing = readCartCodeDeadline();
  bafCartCodeDeadline = existing ? Math.min(existing, candidate) : candidate;
  for (const name of ['localStorage', 'sessionStorage']) {
    try {
      const storage = window[name];
      if (storage.getItem(BAF_CART_CODE_DEADLINE_KEY) !== String(bafCartCodeDeadline)) {
        storage.setItem(BAF_CART_CODE_DEADLINE_KEY, String(bafCartCodeDeadline));
      }
    } catch (_) {}
  }
  if (previous !== bafCartCodeDeadline) window.dispatchEvent(new CustomEvent(BAF_CART_CODE_DEADLINE_EVENT));
  return bafCartCodeDeadline;
}

class BafCartCode extends HTMLElement {
  #resetTimer;
  #copying = false;
  #countdownTimer;
  #deadline = 0;
  #intersectionObserver;
  #dialogObserver;
  #dialog;

  connectedCallback() {
    this.addEventListener('click', this.#handleClick);
    window.addEventListener('storage', this.#handleStorage);
    window.addEventListener('pageshow', this.#syncCountdown);
    window.addEventListener(BAF_CART_CODE_DEADLINE_EVENT, this.#syncCountdown);
    document.addEventListener('visibilitychange', this.#syncCountdown);
    this.#dialog = this.closest('dialog');
    if (this.#dialog) {
      this.#dialog.addEventListener('transitionend', this.#syncCountdown);
      this.#dialog.addEventListener('animationend', this.#syncCountdown);
      this.#dialogObserver = new MutationObserver(this.#syncCountdown);
      this.#dialogObserver.observe(this.#dialog, { attributes: true, attributeFilter: ['open', 'class', 'style'] });
    }
    if ('IntersectionObserver' in window) {
      this.#intersectionObserver = new IntersectionObserver(this.#syncCountdown, { threshold: 0 });
      this.#intersectionObserver.observe(this);
    } else {
      window.addEventListener('scroll', this.#syncCountdown, { passive: true });
      window.addEventListener('resize', this.#syncCountdown);
    }
    this.#syncCountdown();
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#handleClick);
    window.removeEventListener('storage', this.#handleStorage);
    window.removeEventListener('pageshow', this.#syncCountdown);
    window.removeEventListener(BAF_CART_CODE_DEADLINE_EVENT, this.#syncCountdown);
    window.removeEventListener('scroll', this.#syncCountdown);
    window.removeEventListener('resize', this.#syncCountdown);
    document.removeEventListener('visibilitychange', this.#syncCountdown);
    this.#dialog?.removeEventListener('transitionend', this.#syncCountdown);
    this.#dialog?.removeEventListener('animationend', this.#syncCountdown);
    this.#intersectionObserver?.disconnect();
    this.#dialogObserver?.disconnect();
    clearTimeout(this.#resetTimer);
    clearInterval(this.#countdownTimer);
    this.#countdownTimer = null;
  }

  #isVisible() {
    if (!this.isConnected || this.hidden || document.visibilityState === 'hidden') return false;
    if (this.#dialog && !this.#dialog.open) return false;
    if (this.closest('[hidden], [inert], [aria-hidden="true"]')) return false;
    if (this.checkVisibility && !this.checkVisibility({ checkOpacity: true, checkVisibilityCSS: true })) return false;
    const rect = this.getBoundingClientRect();
    return rect.width > 0 && rect.height > 0 && rect.bottom > 0 && rect.right > 0 &&
      rect.top < window.innerHeight && rect.left < window.innerWidth;
  }

  #syncCountdown = () => {
    if (!this.isConnected || this.hasAttribute('data-expired')) return;
    let deadline = readCartCodeDeadline();
    if (!deadline) {
      if (!this.#isVisible()) return;
      deadline = saveCartCodeDeadline(Date.now() + BAF_CART_CODE_DURATION);
    }
    this.#deadline = deadline;
    this.#renderCountdown();
    if (!this.hasAttribute('data-expired') && !this.#countdownTimer) {
      this.#countdownTimer = setInterval(this.#renderCountdown, 1000);
    }
  };

  #handleStorage = (event) => {
    if (event.key !== BAF_CART_CODE_DEADLINE_KEY && event.key !== null) return;
    const candidate = parseCartCodeDeadline(event.newValue);
    if (candidate) saveCartCodeDeadline(candidate);
    this.#syncCountdown();
  };

  #renderCountdown = () => {
    if (!this.isConnected) return;
    const remaining = Math.max(0, Math.ceil((this.#deadline - Date.now()) / 1000));
    const timer = this.querySelector('[data-baf-cart-code-time]');
    const label = String(Math.floor(remaining / 60)).padStart(2, '0') + ':' + String(remaining % 60).padStart(2, '0');
    if (timer && timer.textContent !== label) timer.textContent = label;
    if (!remaining) this.#expire();
  };

  #expire() {
    if (this.hasAttribute('data-expired')) return;
    clearInterval(this.#countdownTimer);
    this.#countdownTimer = null;
    this.#intersectionObserver?.disconnect();
    this.#dialogObserver?.disconnect();
    this.setAttribute('data-expired', '');
  }

  #legacyCopy(input, button) {
    input.focus({ preventScroll: true });
    input.select();
    input.setSelectionRange(0, input.value.length);
    let copied = false;
    try {
      copied = document.execCommand('copy');
    } catch (_) {
      // Keep the code selected for manual copy if the browser blocks copying.
    }
    if (copied) button.focus({ preventScroll: true });
    return copied;
  }

  #handleClick = async (event) => {
    const input = this.querySelector('.baf-cart-code__value');
    if (event.target === input) {
      input.select();
      input.setSelectionRange(0, input.value.length);
      return;
    }
    const button = event.target.closest('.baf-cart-code__copy');
    if (!button || !input || this.#copying) return;
    const label = button.querySelector('[data-copy-label]');
    const status = this.querySelector('.baf-cart-code__status');
    this.#copying = true;
    clearTimeout(this.#resetTimer);
    status.removeAttribute('data-error');
    status.textContent = '';
    let copied = false;
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(input.value);
        copied = true;
      }
    } catch (_) {
      // Some in-app browsers deny Clipboard API access; try a selected input.
    }
    if (!this.isConnected) {
      this.#copying = false;
      return;
    }
    if (!copied) copied = this.#legacyCopy(input, button);
    this.#copying = false;
    if (copied) {
      button.setAttribute('data-copied', '');
      label.textContent = 'Copied';
      status.textContent = 'FORYOU10 copied. Apply it at checkout.';
      this.#resetTimer = setTimeout(() => {
        button.removeAttribute('data-copied');
        label.textContent = 'Copy';
        status.textContent = '';
      }, 3000);
    } else {
      button.removeAttribute('data-copied');
      label.textContent = 'Copy';
      status.setAttribute('data-error', '');
      status.textContent = 'Select FORYOU10 and copy it manually, then apply it at checkout.';
    }
  };
}

if (!customElements.get('baf-cart-code')) {
  customElements.define('baf-cart-code', BafCartCode);
}
